import { useState, useEffect, useRef } from 'react';
import { RotateCcw, CheckCircle } from 'lucide-react';
import { uploadQueue } from '../../services/uploadQueue';
import { useParams } from 'react-router-dom';
import logger from '../../utils/logger';

function ReviewScreen({ mediaBlob, mediaType, guestInfo, onRetake, onUploadComplete }) {
  const { eventId } = useParams();
  const [isConfirming, setIsConfirming] = useState(false);
  const mediaRef = useRef(null);
  const mediaUrlRef = useRef(null);
  const isPhoto = mediaType === 'photo' || mediaBlob?.type?.startsWith('image/');

  useEffect(() => {
    // Create object URL for media preview
    if (mediaBlob) {
      const url = URL.createObjectURL(mediaBlob);
      mediaUrlRef.current = url;

      if (mediaRef.current) {
        mediaRef.current.src = url;
      }
    }

    // Cleanup
    return () => {
      if (mediaUrlRef.current) {
        URL.revokeObjectURL(mediaUrlRef.current);
      }
    };
  }, [mediaBlob]);

  const handleConfirm = () => {
    // Show brief confirmation animation
    setIsConfirming(true);

    // Add media to background upload queue
    const uploadId = uploadQueue.enqueue(
      mediaBlob,
      eventId,
      guestInfo.name,
      guestInfo.email
    );

    logger.log(`Media queued for background upload (ID: ${uploadId})`);

    // Immediately proceed to next screen (upload happens in background)
    setTimeout(() => {
      onUploadComplete();
    }, 800);
  };

  return (
    <div className="fullscreen flex flex-col bg-black">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 p-6 bg-gradient-to-b from-black/80 to-transparent">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white mb-2">
            {isPhoto ? 'Review Your Photo' : 'Review Your Video'}
          </h2>
          <p className="text-neutral-grey-300 text-lg">
            {isPhoto ? 'Happy with this shot?' : 'Happy with your message?'}
          </p>
        </div>
      </div>

      {/* Media Preview */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="max-w-4xl w-full">
          {isPhoto ? (
            <div className="relative w-full max-h-[72vh] bg-black rounded-2xl overflow-hidden shadow-2xl flex items-center justify-center">
              <img
                ref={mediaRef}
                alt="Captured guest memory"
                className="w-full h-full object-contain"
              />
            </div>
          ) : (
            <div className="relative aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl">
              <video
                ref={mediaRef}
                controls
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-full object-contain"
              />
            </div>
          )}
        </div>
      </div>

      {/* Footer Controls */}
      <div className="absolute bottom-0 left-0 right-0 z-10 p-8 bg-gradient-to-t from-black/80 to-transparent">
        <div className="flex gap-4 justify-center">
          {/* Retake Button */}
          <button
            onClick={onRetake}
            disabled={isConfirming}
            className="flex items-center gap-3 px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl transition-all text-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RotateCcw className="w-6 h-6" />
            {isPhoto ? 'Retake Photo' : 'Retake Video'}
          </button>

          {/* Confirm Button */}
          <button
            onClick={handleConfirm}
            disabled={isConfirming}
            className="flex items-center gap-3 px-12 py-4 bg-gold hover:bg-gold-light text-dark-slate font-bold rounded-xl transition-all text-lg shadow-2xl shadow-gold/50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isConfirming ? (
              <>
                <CheckCircle className="w-6 h-6" />
                Confirmed!
              </>
            ) : (
              <>
                <CheckCircle className="w-6 h-6" />
                {isPhoto ? 'Use This Photo' : 'Use This Recording'}
              </>
            )}
          </button>
        </div>

        <p className="text-center text-neutral-grey-400 mt-6">
          {isConfirming
            ? `${isPhoto ? 'Photo' : 'Video'} will upload in the background...`
            : `Take your time to review your ${isPhoto ? 'photo' : 'message'}`}
        </p>
      </div>
    </div>
  );
}

export default ReviewScreen;
